<script setup lang="ts">
import { chartColors } from '@forgd/ui-kit/charts/lib/colors'
import { formatValue } from '@forgd/formatter'
import type { DesignModel, DistributionGroup, TokenEmissionPreview } from '@forgd/contract/types'
import type { WorkingGroup } from '#design-tokenomics/composables/useWorkingGroups'
import { useDesignRoot } from '#design-tokenomics/composables/useDesignRoot'
import { useWorkingGroups } from '#design-tokenomics/composables/useWorkingGroups'
import DtEmissionSchedule from '#design-tokenomics/components/DtEmissionSchedule.vue'
import DtEmissionTableEpoch from '#design-tokenomics/components/DtEmissionTableEpoch.vue'
import DtEmissionTableFullUnlock from '#design-tokenomics/components/DtEmissionTableFullUnlock.vue'
import DtEmissionTableLockVest from '#design-tokenomics/components/DtEmissionTableLockVest.vue'
import DtStatCounter from '#design-tokenomics/components/DtStatCounter.vue'
import { GroupUnlockFrequencyEnum } from '@forgd/shared/enums'
import { Alert, AlertDescription, AlertTitle } from '@forgd/ui-kit/alert'
import { LoadingAnimation } from '@forgd/ui-kit/blocks/animations'
import { Button } from '@forgd/ui-kit/button'
import { Card, CardContent, CardHeader, CardTitle } from '@forgd/ui-kit/card'
import { Dialog, DialogContent } from '@forgd/ui-kit/dialog'
import { Tooltip, TooltipContent, TooltipTrigger } from '@forgd/ui-kit/tooltip'
import { ArrowsUpDownIcon, ExclamationTriangleIcon, InformationCircleIcon, QuestionMarkCircleIcon } from '@heroicons/vue/24/outline'
import { useDebounceFn } from '@vueuse/core'

type EmissionType = 'lock-and-vest' | 'epoch-based' | 'full-initial-unlock'
type GroupWithColor = WorkingGroup & { color: string }

const model = defineModel<DesignModel>({ required: true })

const { sectionPath } = useDesignRoot()
const toast = useAppToast()
const { groups, patchGroup } = useWorkingGroups(model)

const colorById = computed(() => {
  const ranked = [...groups.value].sort((a, b) =>
    a.allocationPercentage === b.allocationPercentage
      ? a.name.localeCompare(b.name)
      : b.allocationPercentage - a.allocationPercentage,
  )

  return new Map(
    ranked.map((group, index) => [
      group.id,
      chartColors.tokenDesigner[index % chartColors.tokenDesigner.length],
    ]),
  )
})

watchEffect(() => {
  for (const group of groups.value) {
    (group as GroupWithColor).color
      = colorById.value.get(group.id) ?? chartColors.tokenDesigner[0]!
  }
})

function groupsOfType(type: EmissionType) {
  return computed<GroupWithColor[]>(() =>
    groups.value.filter(group => group.unlockType === type) as GroupWithColor[],
  )
}

const lockVestGroups = groupsOfType('lock-and-vest')
const epochGroups = groupsOfType('epoch-based')
const fullUnlockGroups = groupsOfType('full-initial-unlock')

const emissionPreview = ref<TokenEmissionPreview | null>(null)
const isPreviewLoading = ref(false)

function validationError(): { title: string, description: string } | null {
  for (const group of groups.value) {
    const cliff = group.unlockAtInitialCliffPercentage ?? 0

    if (group.unlockAtTgePercentage + cliff > 100) {
      return {
        title: `Invalid configuration for group '${group.name}'`,
        description: 'Combined TGE and cliff percentages exceed 100%',
      }
    }

    const lockupInvalid = group.lockupDurationInMonths !== null && group.lockupDurationInMonths < 0
    const tgeInvalid = group.unlockAtTgePercentage < 0 || group.unlockAtTgePercentage > 100
    const cliffInvalid = group.unlockAtInitialCliffPercentage !== null && (cliff < 0 || cliff > 100)

    if (lockupInvalid || tgeInvalid || cliffInvalid) {
      return {
        title: 'Some information provided requires your attention',
        description: 'Please ensure all provided data and calculations are valid before submitting changes.',
      }
    }
  }

  return null
}

async function updateChart() {
  const error = validationError()

  if (error) {
    toast.error({ title: error.title, description: error.description })
    return
  }

  isPreviewLoading.value = true

  try {
    const response = await $fetch<{ emissions: TokenEmissionPreview }>('/calculator/percentage-emissions', {
      method: 'POST',
      body: { groups: groups.value, total_supply: model.value.MTS },
    })

    emissionPreview.value = response.emissions
  }
  catch {
    // The chart is a preview; a calculator outage should not block editing the schedule.
    emissionPreview.value = null
  }
  finally {
    isPreviewLoading.value = false
  }
}

const previewDebounced = useDebounceFn(
  () => updateChart(),
  useRuntimeConfig().public.featureFlags.calculator.debounceMs,
)

watch(groups, () => previewDebounced(), { deep: true })
onMounted(() => updateChart())

const moveGroupId = ref<string | null>(null)
const movingFrom = ref<EmissionType | null>(null)

const MOVE_LABELS: Record<EmissionType, string> = {
  'lock-and-vest': 'Lock & Vest',
  'epoch-based': 'Epoch-Based',
  'full-initial-unlock': 'Full Initial Unlock',
}

const MOVE_DEFAULTS: Record<EmissionType, Partial<DistributionGroup>> = {
  'lock-and-vest': {
    lockupDurationInMonths: 12,
    unlockDurationInMonths: 36,
    unlockAtInitialCliffPercentage: 0,
    unlockFrequency: GroupUnlockFrequencyEnum.Monthly,
    epochInMonths: null,
    firstEpochPercentage: null,
    reductionsInEpochPercentage: null,
  },
  'epoch-based': {
    lockupDurationInMonths: null,
    unlockDurationInMonths: null,
    unlockAtInitialCliffPercentage: 0,
    unlockFrequency: GroupUnlockFrequencyEnum.Monthly,
    epochInMonths: 6,
    firstEpochPercentage: 50,
    reductionsInEpochPercentage: 50,
  },
  'full-initial-unlock': {
    lockupDurationInMonths: null,
    unlockDurationInMonths: null,
    unlockAtInitialCliffPercentage: null,
    unlockFrequency: null,
    epochInMonths: null,
    firstEpochPercentage: null,
    reductionsInEpochPercentage: null,
  },
}

function askToMove(id: string, from: EmissionType) {
  moveGroupId.value = id
  movingFrom.value = from
}

function moveGroupTo(type: EmissionType) {
  if (!moveGroupId.value) {
    return
  }

  patchGroup(moveGroupId.value, {
    unlockType: type,
    dictateDuration: false,
    delayInMonths: null,
    durationInMonths: null,
    fullUnlock: false,
    ...MOVE_DEFAULTS[type],
  })

  moveGroupId.value = null
  movingFrom.value = null
}
</script>

<template>
  <div class="space-y-5">
    <Alert size="sm" class="text-sm">
      <InformationCircleIcon />
      <AlertDescription>
        To edit, add, or remove groups, go to
        <NuxtLink :to="sectionPath('/modeling/token-distribution-schedule')">
          Token Distribution Schedule
        </NuxtLink>
      </AlertDescription>
    </Alert>

    <Card class="min-h-85">
      <CardHeader>
        <span class="flex items-center gap-1">
          <CardTitle class="text-sm">Token Emission Schedule</CardTitle>
          <Tooltip>
            <TooltipTrigger type="button" aria-label="About token emission schedule" class="inline-flex">
              <QuestionMarkCircleIcon class="size-4 shrink-0 text-primary-300" aria-hidden="true" />
            </TooltipTrigger>
            <TooltipContent>
              Your Total Emission Duration might exceed what is shown on the chart. This can be
              caused by epoch based duration taking a long time to fully emit. The Total Emission
              Duration will always show how long it takes until 99.9% of your tokens are emitted
            </TooltipContent>
          </Tooltip>
        </span>
      </CardHeader>
      <CardContent class="p-0">
        <div v-if="isPreviewLoading && !emissionPreview" class="m-auto flex min-h-55 items-center justify-center">
          <LoadingAnimation />
        </div>
        <DtEmissionSchedule v-else :emission-schedule="emissionPreview" />
      </CardContent>
    </Card>

    <Card>
      <CardContent>
        <div class="flex justify-between gap-6 @max-xl:flex-col @max-xl:gap-4">
          <DtStatCounter
            title="Total Emission Duration"
            :value="formatValue(emissionPreview?.totalEmissionDuration ?? 0, 'string:plural,month')"
            tooltip="The Total Emission Duration always shows how long it takes until 99.9% of your tokens are emitted."
          />
          <DtStatCounter
            title="% of Total Token Supply Unlocked at TGE"
            :value="formatValue(emissionPreview?.percentageUnlockedAtTGE ?? 0, 'percent:2')"
          />
          <DtStatCounter
            title="Avg. Annual Inflation"
            :value="formatValue(emissionPreview?.averageAnnualInflation ?? 0, 'percent:2')"
          />
        </div>
      </CardContent>
    </Card>

    <Card>
      <CardHeader class="px-4">
        <div class="text-xs leading-normal">
          <CardTitle>
            Lock &amp; Vest Emission Groups
          </CardTitle>
          <div class="text-gray-600">
            Groups that unlock linearly after a cliff.
          </div>
        </div>
        <DtStatCounter title="Groups" :value="lockVestGroups.length" size="md" />
      </CardHeader>
      <CardContent class="p-0">
        <DtEmissionTableLockVest :groups="lockVestGroups" @move-group="askToMove" />
      </CardContent>
    </Card>

    <Card>
      <CardHeader class="px-4">
        <div class="text-xs leading-normal">
          <CardTitle>
            Epoch-Based Emission Groups
          </CardTitle>
          <div class="text-gray-600">
            Groups that emit in a logarithmic fashion.
          </div>
        </div>
        <DtStatCounter title="Groups" :value="epochGroups.length" size="md" />
      </CardHeader>
      <CardContent class="p-0">
        <DtEmissionTableEpoch :groups="epochGroups" @move-group="askToMove" />
      </CardContent>
    </Card>

    <Card>
      <CardHeader class="px-4">
        <div class="text-xs leading-normal">
          <CardTitle>
            Full Initial Unlock Emission Groups
          </CardTitle>
          <div class="text-gray-600">
            Groups that unlock 100% of the tokens at TGE.
          </div>
        </div>
        <DtStatCounter title="Groups" :value="fullUnlockGroups.length" size="md" />
      </CardHeader>
      <CardContent class="p-0">
        <DtEmissionTableFullUnlock :groups="fullUnlockGroups" @move-group="askToMove" />
      </CardContent>
    </Card>

    <Alert v-if="emissionPreview?.percentageUnlockedAtTGE === 0" variant="warning">
      <ExclamationTriangleIcon />
      <AlertTitle>None of your groups unlock tokens at TGE</AlertTitle>
      <AlertDescription>
        <p>Please review all details before saving.</p>
      </AlertDescription>
    </Alert>
  </div>

  <Dialog :open="Boolean(moveGroupId)" @update:open="(open) => !open && (moveGroupId = null)">
    <DialogContent>
      <div class="flex flex-col items-center justify-center space-y-10">
        <div class="space-y-2.5">
          <ArrowsUpDownIcon class="mb-4.25 size-10 shrink-0 text-primary-300" aria-hidden="true" />
          <div class="text-xl font-semibold text-primal">
            You are about to move this group from the {{ movingFrom ? MOVE_LABELS[movingFrom] : '' }} Schedule.
          </div>
          <div class="text-gray-600">
            Where do you want to move this group?
          </div>
        </div>

        <div class="grid w-full grid-cols-2 gap-4">
          <Button v-if="movingFrom !== 'epoch-based'" variant="outline" block @click="moveGroupTo('epoch-based')">
            Epoch-Based Schedule
          </Button>
          <Button v-if="movingFrom !== 'lock-and-vest'" variant="outline" block @click="moveGroupTo('lock-and-vest')">
            Lock &amp; Vest Schedule
          </Button>
          <Button v-if="movingFrom !== 'full-initial-unlock'" variant="outline" block @click="moveGroupTo('full-initial-unlock')">
            Full Initial Unlock
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>
